/**
 * The route table.
 *
 * Exported as a plain array rather than built into a router here, so tests can
 * mount the same table under a memory router. A test that asserts against a
 * route tree it constructed itself proves nothing about the one that ships.
 *
 * §3's three primary destinations plus `/setup` exist so far in Phase 0. The
 * rest of §3's table — `/session/:id`, `/settings/*`, `/developer` — arrives
 * with the features behind it. `/` is Home (#442) because the server's
 * SPA fallback serves `index.html` at `/`, and a bare root that renders
 * nothing would be the first thing the founder sees after launching.
 */

import { Navigate, useLocation, type RouteObject } from 'react-router';
import { AppShell } from './app-shell.js';
import { AuthLayout } from './auth-layout.js';
import { RouteError } from './route-error.js';
import { HomePage } from '../features/home/home-page.js';
import { TextbookPage } from '../features/curriculum/textbook-page.js';
import { ProgressPage } from '../features/progress/progress-page.js';
import { DataPage } from '../features/settings/data-page.js';
import { ConsentPage } from '../features/consent/consent-page.js';
import { LoginPage } from '../features/auth/login-page.js';
import { SignupPage } from '../features/auth/signup-page.js';
import { WelcomePage } from '../features/setup/welcome-page.js';
import { useIsPhone } from './use-is-phone.js';
import { SettingsLayout } from '../features/settings/settings-layout.js';
import { AccountPage } from '../features/settings/account-page.js';
import { CoursesPage } from '../features/settings/courses-page.js';
import { ContactPage } from '../features/settings/contact-page.js';
import { HomeworkUploadPage } from '../features/homework/upload-page.js';
import { HomeworkPage } from '../features/homework/homework-page.js';
import { HomeworkFeedbackPage } from '../features/homework/feedback-page.js';
import { PrepPage } from '../features/prep/prep-page.js';
import { SessionPage } from '../features/prep/session-page.js';

/**
 * The primary navigation destinations (§3: three until #376 / D30 added
 * Homework as a fourth, 2026-10-18). Used both for the shell's
 * nav and for the a11y assertions that are specifically about *that* nav —
 * `aria-current` and "every primary destination is reachable by keyboard from
 * any of the others." `/setup` is deliberately not here: it is not one of
 * §3's destinations, and adding it would make those two assertions
 * false rather than more thorough.
 */
export const PRIMARY_ROUTES = ['/', '/homework', '/progress', '/exam-prep'] as const;

/**
 * Every route the a11y harness must cover — the complete route table, not
 * just the nav. `routes.a11y.test.tsx` and `no-percentage.test.tsx` iterate
 * this one for the assertions that apply to any screen (no violations,
 * exactly one h1, no percentage), and `PRIMARY_ROUTES` for the assertions
 * that are specifically about primary navigation. A route added to the table
 * below without a matching entry here would render unchecked, which is the
 * drift both harnesses exist to prevent.
 */
export const A11Y_ROUTES = [
  ...PRIMARY_ROUTES,
  // #442: Textbook moved to the secondary navigation; the route remains.
  '/textbook',
  '/settings/account',
  '/settings/courses',
  '/settings/data',
  '/settings/contact',
  // #373: review and withdraw consent; also the screen the gate shows first.
  '/settings/consent',
  // #648: the last two signup steps (Moodle, Courses), after the consent gate's first save.
  '/welcome',
  // #371: outside the app shell, reachable with no session.
  '/login',
  '/signup',
] as const;

/**
 * Routes in the table that need data from the URL and so cannot be rendered by
 * path alone. The a11y harness cannot iterate them, so each is named here and
 * covered by its own component tests; `routes.a11y.test.tsx` checks that this
 * list and `A11Y_ROUTES` together are exactly the route table, so a
 * parameterised route cannot be added without being named somewhere.
 */
export const PARAMETERISED_ROUTES = ['/homework/:uploadId', '/homework/:uploadId/feedback', '/session/:sessionId'] as const;

/** #550: routes that only redirect (Today left the web: the pilot is maths only, D30), so nothing renders at them. */
export const REDIRECT_ROUTES = ['/today', '/setup', '/settings'] as const;

/**
 * #581: `/setup` is gone. Its Moodle section moved to Account and its courses to Courses and colours, so the old
 * link (Home's nudge still uses `#moodle-connection`) goes to the new home of whatever it pointed at; the hash is
 * kept so the target still scrolls.
 */
function SetupRedirect(): React.JSX.Element {
  const { hash } = useLocation();
  const to = hash === '#moodle-connection' ? '/settings/account#moodle-connection' : `/settings/courses${hash}`;
  return <Navigate to={to} replace />;
}

/** `/settings`: on desktop it opens Account; on a phone it is the list of pages, which the layout draws (#648). */
function SettingsIndex(): React.JSX.Element | null {
  return useIsPhone() ? null : <Navigate to="/settings/account" replace />;
}

export const routes: RouteObject[] = [
  {
    path: '/',
    element: <AppShell />,
    // Attached to the shell rather than to each leaf: a thrown error is caught
    // at the nearest boundary, so this keeps the header and navigation on
    // screen while a single destination fails. §6.4.
    errorElement: <RouteError />,
    children: [
      // #442: Home, never filtered by the course focus.
      { index: true, element: <HomePage /> },
      { path: 'today', element: <Navigate to="/" replace /> },
      { path: 'textbook', element: <TextbookPage /> },
      { path: 'progress', element: <ProgressPage /> },
      // #376 (D30): handwritten homework - upload, then check what was read.
      { path: 'homework', element: <HomeworkUploadPage /> },
      { path: 'homework/:uploadId', element: <HomeworkPage /> },
      // #377 (U1): what a check of the upload shows, opened on request.
      { path: 'homework/:uploadId/feedback', element: <HomeworkFeedbackPage /> },
      // #440, #362: exam prep - the plan, then one session at a time.
      { path: 'exam-prep', element: <PrepPage /> },
      { path: 'session/:sessionId', element: <SessionPage /> },
      { path: 'setup', element: <SetupRedirect /> },
      { path: 'welcome', element: <WelcomePage /> },
      // #581: Settings, one layout with a sub-menu. `/settings/learning` and `/settings/model` arrive with what is
      // behind them. `/settings/data` and `/settings/consent` are the screens that were top-level routes before.
      {
        path: 'settings',
        element: <SettingsLayout />,
        children: [
          { index: true, element: <SettingsIndex /> },
          { path: 'account', element: <AccountPage /> },
          { path: 'courses', element: <CoursesPage /> },
          // #373 (D30): consent per purpose, for review and withdrawal.
          { path: 'consent', element: <ConsentPage /> },
          { path: 'data', element: <DataPage /> },
          { path: 'contact', element: <ContactPage /> },
        ],
      },
      // #480: founder only; the link is in the secondary navigation only for the founder.
    ],
  },
  // #371 (D30): sign-in and sign-up, outside the app shell. They render with no
  // session and make no API request on load; the server serves the SPA shell to
  // anyone (session.ts), and every API call behind it still demands a session.
  {
    element: <AuthLayout />,
    errorElement: <RouteError />,
    children: [
      { path: '/login', element: <LoginPage /> },
      { path: '/signup', element: <SignupPage /> },
    ],
  },
];
